import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  Tag,
  Tabs,
  Alert,
  Space,
  Typography,
  message,
  Modal,
  Timeline,
  Spin,
} from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  AndroidOutlined,
  AppleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';

const { Title, Text } = Typography;

interface StoreStats {
  enabled: boolean;
  lastSyncedAt: string | null;
  totalProducts: number;
  synced: number;
  mismatches: number;
  storeOnly: number;
  localOnly: number;
  errors: number;
}

interface SyncStatusResponse {
  googlePlay: StoreStats;
  appStore: StoreStats;
  overall: { healthy: boolean; lastFullSync: string | null; issueCount: number };
}

interface ProductWithSync {
  id: string;
  name: string;
  storeProductId: string;
  category: string;
  platform: string;
  isActive: boolean;
  googlePlay?: {
    syncStatus: string;
    lastSyncedAt: string | null;
    hasIssue: boolean;
    issue?: string;
  };
  appStore?: { syncStatus: string; lastSyncedAt: string | null; hasIssue: boolean; issue?: string };
}

interface MismatchItem {
  productId: string;
  productName: string;
  store: string;
  storeProductId: string;
  mismatchDetails: {
    fields: string[];
    localValues: Record<string, unknown>;
    storeValues: Record<string, unknown>;
  };
}

interface SyncHistoryItem {
  id: string;
  store: string;
  triggeredAt: string;
  triggeredBy: string;
  result: { success: boolean; synced?: number; errors?: number; error?: string };
  duration?: number;
}

interface StoreOnlyProduct {
  store: string;
  storeProductId: string;
  storeData: Record<string, unknown>;
  lastSyncedAt: string | null;
}

const statusColor = (s: string) => {
  switch (s) {
    case 'synced':
      return 'success';
    case 'mismatch':
      return 'error';
    case 'store_only':
      return 'warning';
    case 'local_only':
      return 'blue';
    case 'error':
      return 'red';
    default:
      return 'default';
  }
};

const StoreSyncPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatusResponse | null>(null);
  const [products, setProducts] = useState<ProductWithSync[]>([]);
  const [history, setHistory] = useState<SyncHistoryItem[]>([]);
  const [mismatches, setMismatches] = useState<MismatchItem[]>([]);
  const [storeOnly, setStoreOnly] = useState<StoreOnlyProduct[]>([]);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('products');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, productsRes, historyRes, mismatchesRes, storeOnlyRes] =
        await Promise.allSettled([
          adminApi.getStoreSyncStatus(),
          adminApi.getStoreSyncProducts(),
          adminApi.getStoreSyncHistory(20),
          adminApi.getStoreSyncMismatches(),
          adminApi.getStoreOnlyProducts(),
        ]);

      if (statusRes.status === 'fulfilled') setSyncStatus(statusRes.value.data);
      if (productsRes.status === 'fulfilled') {
        const d = productsRes.value.data;
        setProducts(Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : []);
      }
      if (historyRes.status === 'fulfilled') {
        const d = historyRes.value.data;
        setHistory(Array.isArray(d) ? d : []);
      }
      if (mismatchesRes.status === 'fulfilled') {
        const d = mismatchesRes.value.data;
        setMismatches(Array.isArray(d) ? d : []);
      }
      if (storeOnlyRes.status === 'fulfilled') {
        const d = storeOnlyRes.value.data;
        setStoreOnly(Array.isArray(d) ? d : []);
      }
    } catch (error) {
      console.error('Failed to fetch store sync data', error);
      message.error('Failed to load store sync data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async (store?: 'google_play' | 'app_store') => {
    setSyncing(store || 'all');
    try {
      const res = await adminApi.triggerStoreSync(store);
      const data = res.data;
      const gpCount = data?.googlePlay?.synced || 0;
      const asCount = data?.appStore?.synced || 0;
      message.success(`Sync complete: ${gpCount + asCount} products synced`);
      await fetchData();
    } catch (error) {
      console.error('Sync failed', error);
      message.error('Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const handleValidate = async () => {
    try {
      const res = await adminApi.validateStoreCatalogs();
      Modal.info({
        title: 'Catalog Validation Results',
        width: 600,
        content: (
          <div>
            <Alert
              type={res.data.valid ? 'success' : 'warning'}
              message={res.data.valid ? 'All catalogs valid' : 'Issues found'}
              style={{ marginBottom: 16 }}
            />
            {['googlePlay', 'appStore'].map((store) => {
              const storeData = res.data[store];
              const label = store === 'googlePlay' ? 'Google Play' : 'App Store';
              return (
                <div key={store}>
                  <Title level={5}>{label}</Title>
                  {storeData?.issues?.length > 0 ? (
                    storeData.issues.map(
                      (
                        issue: { productId: string; issue: string; severity: string },
                        i: number
                      ) => (
                        <Alert
                          key={i}
                          type={issue.severity === 'error' ? 'error' : 'warning'}
                          message={`${issue.productId}: ${issue.issue}`}
                          style={{ marginBottom: 8 }}
                        />
                      )
                    )
                  ) : (
                    <Text type="success">No issues</Text>
                  )}
                </div>
              );
            })}
          </div>
        ),
      });
    } catch (error) {
      console.error('Validation failed', error);
      message.error('Validation failed');
    }
  };

  const gp = syncStatus?.googlePlay;
  const as = syncStatus?.appStore;
  const totalProducts = (gp?.totalProducts || 0) + (as?.totalProducts || 0);
  const totalSynced = (gp?.synced || 0) + (as?.synced || 0);
  const totalMismatches = (gp?.mismatches || 0) + (as?.mismatches || 0);
  const totalStoreOnly = (gp?.storeOnly || 0) + (as?.storeOnly || 0);

  if (loading && !syncStatus) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Store Sync
          </Title>
          <Text type="secondary">
            Health:{' '}
            <Tag color={syncStatus?.overall?.healthy ? 'green' : 'red'}>
              {syncStatus?.overall?.healthy
                ? 'Healthy'
                : `${syncStatus?.overall?.issueCount} issues`}
            </Tag>
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              Refresh
            </Button>
            <Button onClick={handleValidate}>Validate Catalogs</Button>
            <Button
              type="primary"
              icon={<SyncOutlined spin={syncing === 'all'} />}
              onClick={() => handleSync()}
              loading={syncing === 'all'}
            >
              Sync All
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total Products" value={totalProducts} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Synced"
              value={totalSynced}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Mismatches"
              value={totalMismatches}
              valueStyle={{ color: totalMismatches ? '#cf1322' : undefined }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Store Only"
              value={totalStoreOnly}
              valueStyle={{ color: totalStoreOnly ? '#faad14' : undefined }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Per-store status */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card
            size="small"
            title={
              <Space>
                <AndroidOutlined /> Google Play
                {gp?.enabled ? <Tag color="green">Enabled</Tag> : <Tag>Disabled</Tag>}
              </Space>
            }
            extra={
              <Button
                size="small"
                icon={<SyncOutlined spin={syncing === 'google_play'} />}
                onClick={() => handleSync('google_play')}
                loading={syncing === 'google_play'}
              >
                Sync
              </Button>
            }
          >
            <Space direction="vertical" size={4}>
              <Text>
                Synced: {gp?.synced || 0} | Mismatches: {gp?.mismatches || 0} | Errors:{' '}
                {gp?.errors || 0}
              </Text>
              <Text type="secondary">
                Last sync: {gp?.lastSyncedAt ? new Date(gp.lastSyncedAt).toLocaleString() : 'Never'}
              </Text>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card
            size="small"
            title={
              <Space>
                <AppleOutlined /> App Store
                {as?.enabled ? <Tag color="green">Enabled</Tag> : <Tag>Disabled</Tag>}
              </Space>
            }
            extra={
              <Button
                size="small"
                icon={<SyncOutlined spin={syncing === 'app_store'} />}
                onClick={() => handleSync('app_store')}
                loading={syncing === 'app_store'}
              >
                Sync
              </Button>
            }
          >
            <Space direction="vertical" size={4}>
              <Text>
                Synced: {as?.synced || 0} | Mismatches: {as?.mismatches || 0} | Errors:{' '}
                {as?.errors || 0}
              </Text>
              <Text type="secondary">
                Last sync: {as?.lastSyncedAt ? new Date(as.lastSyncedAt).toLocaleString() : 'Never'}
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab={`Products (${products.length})`} key="products">
            <Table dataSource={products} rowKey="id" pagination={{ pageSize: 20 }} size="small">
              <Table.Column
                title="Product ID"
                dataIndex="storeProductId"
                render={(v) => <code style={{ fontSize: 11 }}>{v}</code>}
              />
              <Table.Column title="Name" dataIndex="name" />
              <Table.Column title="Category" dataIndex="category" render={(v) => <Tag>{v}</Tag>} />
              <Table.Column
                title="Google Play"
                dataIndex="googlePlay"
                render={(gp: ProductWithSync['googlePlay']) =>
                  gp ? (
                    <Tag color={statusColor(gp.syncStatus)}>{gp.syncStatus}</Tag>
                  ) : (
                    <Tag>N/A</Tag>
                  )
                }
              />
              <Table.Column
                title="App Store"
                dataIndex="appStore"
                render={(as: ProductWithSync['appStore']) =>
                  as ? (
                    <Tag color={statusColor(as.syncStatus)}>{as.syncStatus}</Tag>
                  ) : (
                    <Tag>N/A</Tag>
                  )
                }
              />
              <Table.Column
                title="Active"
                dataIndex="isActive"
                render={(v) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>)}
              />
            </Table>
          </Tabs.TabPane>

          <Tabs.TabPane tab={`Mismatches (${mismatches.length})`} key="mismatches">
            {mismatches.length === 0 ? (
              <Alert type="success" message="No mismatches found" />
            ) : (
              <Table
                dataSource={mismatches}
                rowKey="productId"
                pagination={{ pageSize: 10 }}
                size="small"
              >
                <Table.Column title="Product" dataIndex="productName" />
                <Table.Column
                  title="Store"
                  dataIndex="store"
                  render={(s) => (
                    <Tag icon={s === 'google_play' ? <AndroidOutlined /> : <AppleOutlined />}>
                      {s === 'google_play' ? 'Google Play' : 'App Store'}
                    </Tag>
                  )}
                />
                <Table.Column
                  title="Mismatched Fields"
                  dataIndex="mismatchDetails"
                  render={(d) =>
                    d?.fields?.map((f: string) => (
                      <Tag key={f} color="red">
                        {f}
                      </Tag>
                    )) || '-'
                  }
                />
                <Table.Column
                  title="Local"
                  dataIndex="mismatchDetails"
                  render={(d) =>
                    d?.localValues ? (
                      <code style={{ fontSize: 11 }}>{JSON.stringify(d.localValues)}</code>
                    ) : (
                      '-'
                    )
                  }
                />
                <Table.Column
                  title="Store"
                  dataIndex="mismatchDetails"
                  render={(d) =>
                    d?.storeValues ? (
                      <code style={{ fontSize: 11 }}>{JSON.stringify(d.storeValues)}</code>
                    ) : (
                      '-'
                    )
                  }
                />
              </Table>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab={`Store Only (${storeOnly.length})`} key="store-only">
            {storeOnly.length === 0 ? (
              <Alert type="success" message="No store-only products found" />
            ) : (
              <Table
                dataSource={storeOnly}
                rowKey="storeProductId"
                pagination={{ pageSize: 10 }}
                size="small"
              >
                <Table.Column title="Store Product ID" dataIndex="storeProductId" />
                <Table.Column
                  title="Store"
                  dataIndex="store"
                  render={(s) => (
                    <Tag icon={s === 'google_play' ? <AndroidOutlined /> : <AppleOutlined />}>
                      {s === 'google_play' ? 'Google Play' : 'App Store'}
                    </Tag>
                  )}
                />
                <Table.Column
                  title="Last Synced"
                  dataIndex="lastSyncedAt"
                  render={(v) => (v ? new Date(v).toLocaleString() : '-')}
                />
                <Table.Column
                  title="Store Data"
                  dataIndex="storeData"
                  render={(d) =>
                    d ? (
                      <code style={{ fontSize: 10 }}>{JSON.stringify(d).slice(0, 100)}...</code>
                    ) : (
                      '-'
                    )
                  }
                />
              </Table>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab="Sync History" key="history">
            {history.length === 0 ? (
              <Alert type="info" message="No sync history yet" />
            ) : (
              <Timeline
                items={history.map((item) => ({
                  dot: item.result?.success ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  ),
                  children: (
                    <div>
                      <Text strong>
                        {item.store === 'google_play'
                          ? 'Google Play'
                          : item.store === 'app_store'
                            ? 'App Store'
                            : item.store}
                      </Text>
                      <Text type="secondary"> — {new Date(item.triggeredAt).toLocaleString()}</Text>
                      <br />
                      <Text type="secondary">
                        By: {item.triggeredBy} | Synced: {item.result?.synced || 0} | Errors:{' '}
                        {item.result?.errors || 0}
                        {item.duration ? ` | ${item.duration}ms` : ''}
                      </Text>
                      {item.result?.error && (
                        <Alert type="error" message={item.result.error} style={{ marginTop: 4 }} />
                      )}
                    </div>
                  ),
                }))}
              />
            )}
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default StoreSyncPage;
